// "This report covers 3 individuals. Which one is this Test for?" (TGCLOUD-1868, Phase E4; plan,
// "Upload of a multi-individual report on a Test"). Drawn at the head of the Test pane for an
// uploaded report that gives results for two or more people, while the report is shown beside the
// Test (the verification view).
//
// Each person is listed by role, with their Result cell as printed and their row in the report: the
// row is drawn in the report pane, "Show in the report" goes to it, and choosing a person draws it
// strongly there. NOTHING IS PRESELECTED. The one the identity check proposes is labelled "Matches
// this person’s details", and is still the clinician's to choose. Who the Test cannot take (a fetus
// on a Test not of its fetus record, anyone else on a Test about a pregnancy) is listed apart, not
// to choose, with why (whichIndividual.js). Reading the report for the person chosen
// (useLabReportReview.chooseIndividual) is a deliberate second step; until then nothing of the
// report is on the Test, Save waits, and Dismiss takes the report away.
//
// "SEND TO THE INBOX INSTEAD" is offered here too, to a caller the inbox admits: a report that is not
// this person's at all goes to the Lab Report Inbox, where each person on it is matched on their own.
// This question carries no reading's token, so the file itself is sent, read for nobody
// (useLabReportReview.sendChoiceToInbox): nobody need be chosen first, and nothing of it is applied.
// It used to be read for a person chosen only to get a token (TGCLOUD-1868 review).
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { useVerification } from './useVerificationView';
import { SHOW_IN_REPORT } from './labReportVerificationVocabulary';
import {
  PROPOSED_LABEL,
  RESULT_AS_PRINTED,
  SEND_CHOICE_TO_INBOX_TEXT,
  choiceOf,
  readIntoTestLabel
} from './whichIndividual';
import { useAutoTranslation } from 'src/hooks/useTranslate';

function Person({ entry, view, control = null }) {
  const t = useAutoTranslation();

  const located = entry.paths.length > 0 && !!view?.active;
  const words = (
    <Box sx={{ py: 0.25 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {entry.label}
        </Typography>
        {entry.proposed && (
          <Chip
            size="small"
            variant="outlined"
            label={t(PROPOSED_LABEL)}
            data-testid="individual-proposed"
          />
        )}
      </Box>
      {entry.resultAsPrinted && (
        <Typography
          variant="caption"
          component="div"
          sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}
        >
          {t(RESULT_AS_PRINTED)}: {entry.resultAsPrinted}
        </Typography>
      )}
    </Box>
  );
  return (
    <Box
      data-testid="individual-option"
      data-individual={entry.individual.key}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap'
      }}
    >
      {control ? (
        <FormControlLabel
          value={entry.individual.key}
          control={control}
          label={words}
          sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
        />
      ) : (
        words
      )}
      {located && (
        <Button
          size="small"
          variant="text"
          startIcon={<CropFreeIcon />}
          aria-label={`${t(SHOW_IN_REPORT)}: ${entry.label}`}
          onClick={() => view.select(entry.paths, 'value')}
          sx={{ textTransform: 'none' }}
        >
          {t(SHOW_IN_REPORT)}
        </Button>
      )}
    </Box>
  );
}

/**
 * "Send to the inbox instead", during the choice (TGCLOUD-1868, Phase E routine call): the file
 * itself, read for nobody, whoever is chosen or fits the Test (TGCLOUD-1868 review).
 */
function SendToInbox({ review, onSend }) {
  const t = useAutoTranslation();

  const canSend = review?.sending !== true;
  return (
    <Box
      data-testid="choice-send-to-inbox"
      sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
      >
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          disabled={!canSend}
          onClick={() => onSend()}
          startIcon={review?.sending ? <CircularProgress size={14} /> : null}
        >
          {t('Send to the inbox instead')}
        </Button>
        <Typography variant="caption" component="div">
          {t(SEND_CHOICE_TO_INBOX_TEXT)}
        </Typography>
      </Box>
      {review?.sendError && (
        <Typography
          variant="body2"
          sx={{ mt: 0.5, fontWeight: 600 }}
          role="alert"
        >
          {review.sendError.message}
        </Typography>
      )}
    </Box>
  );
}

/**
 * @param {object} props
 * @param {object} props.review        a `choosing` review (useLabReportReview): `choice` is
 *   { individuals, proposedKey, token? }, `error` a refused choice, said; `sending` and `sendError`
 *   while it is sent to the inbox
 * @param {{ pregnancyId?, fetusId? }} props.testScope the Test's prenatal scope
 * @param {(individual: { key, role }) => void} props.onChoose  read the report for that person
 * @param {() => void} props.onDismiss
 * @param {() => void} [props.onSendToInbox] "Send to the inbox instead": the file itself, read for
 *   nobody; offered only when given (an uploaded report, and a caller the inbox admits)
 */
export default function IndividualChoice({
  review,
  testScope,
  onChoose,
  onDismiss,
  onSendToInbox
}) {
  const t = useAutoTranslation();

  const view = useVerification();
  const questionId = React.useId();
  const [chosen, setChosen] = React.useState('');
  const individuals = review?.choice?.individuals;
  const proposedKey = review?.choice?.proposedKey || null;
  const pregnancyId = testScope?.pregnancyId;
  const fetusId = testScope?.fetusId;
  const choice = React.useMemo(
    () =>
      choiceOf({
        individuals,
        proposedKey,
        testScope: { pregnancyId, fetusId },
        t
      }),
    [individuals, proposedKey, pregnancyId, fetusId, t]
  );
  const chosenEntry =
    choice.offered.find((entry) => entry.individual.key === chosen) || null;
  const error = review?.error || null;

  const choose = (key) => {
    setChosen(key);
    const entry = choice.offered.find((e) => e.individual.key === key);
    // Drawn strongly in the report, where it is: the view's own pointer, nothing else moves.
    if (entry && entry.paths.length && view?.active)
      view.highlight(entry.paths);
  };

  return (
    <Box data-testid="individual-choice">
      <Typography
        id={questionId}
        variant="body2"
        component="h4"
        sx={{ fontWeight: 700, mb: 0.5 }}
      >
        {choice.question}
      </Typography>
      <Typography
        variant="caption"
        component="div"
        sx={{ color: 'text.secondary', mb: 1 }}
      >
        {t(
          'Each person’s result is marked in the report beside this Test. Nothing from the report is put on the Test until you choose.'
        )}
      </Typography>
      {error && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          data-testid="individual-choice-refused"
        >
          {error.message}
          {(error.reasons || []).map((reason) => (
            <Typography key={reason} variant="body2">
              {reason}
            </Typography>
          ))}
        </Alert>
      )}
      {choice.offered.length > 0 && (
        <RadioGroup
          aria-labelledby={questionId}
          value={chosen}
          onChange={(e) => choose(e.target.value)}
        >
          {choice.offered.map((entry) => (
            <Person
              key={entry.individual.key}
              entry={entry}
              view={view}
              control={<Radio size="small" />}
            />
          ))}
        </RadioGroup>
      )}
      {choice.notOffered.length > 0 && (
        <Box
          data-testid="individuals-not-offered"
          sx={{
            mt: 1,
            pt: 1,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography
            variant="caption"
            component="div"
            sx={{ color: 'text.secondary', mb: 0.5 }}
            data-testid="individuals-not-offered-why"
          >
            {choice.whyNotOffered}
          </Typography>
          {choice.notOffered.map((entry) => (
            <Person key={entry.individual.key} entry={entry} view={view} />
          ))}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 1,
          mt: 1.5
        }}
      >
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={onDismiss}
        >
          {t('Dismiss')}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={!chosenEntry || review?.sending === true}
          data-testid="read-for-individual"
          onClick={() => chosenEntry && onChoose(chosenEntry.individual)}
        >
          {readIntoTestLabel(chosenEntry?.individual, t)}
        </Button>
      </Box>
      {onSendToInbox && <SendToInbox review={review} onSend={onSendToInbox} />}
    </Box>
  );
}
